import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import Portal from '@mui/material/Portal'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { isStaff } from '@/utils/type-guards'

import colors from '@/assets/scss/_themes-vars.module.scss'

import DoctorIcon from '@/components/ui/icons/Doctor'
import PdsIcon from '@/components/ui/icons/Pds'

import { AuthUser } from '@/core/classes/Auth'
import { Consultation, ConsultationStatus } from '@/core/models/consultation.model'

import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'
import TopbarButton from '@/layouts/diagnostic/TopbarButton'

import { useAppDispatch } from '@/redux/rootReducer'
import { confirmReadingMessages } from '@/redux/reducers/chat-room.reducer'

import EndLiveConsultationDialog from '@/pages/diagnostic/containers/EndLiveConsultationDialog'
import InvitationDialog from '@/pages/diagnostic/containers/InvitationDialog'
import Messenger from '@/pages/diagnostic/containers/Messenger'
import MeetingClockComponent from '@/pages/diagnostic/components/MeetingClock'
import NetworkSpeed from '@/pages/diagnostic/containers/NetworkSpeed'
import WaitingPatientsMiniView from '@/pages/diagnostic/components/WaitingPatientsMiniView'

import { ReadinessChecklistState } from '../model/useReadinessChecklist'
import { ReadinessChecklistButton } from './ReadinessChecklistButton'

type TopbarProps = {
  authUser: AuthUser
  consultation: Consultation
  consultationId?: string
  doctor: string
  isConsultationActive: boolean
  isDoctorOnline: boolean
  isDoctor: boolean
  isPdsOnline: boolean
  pds: string
  readOnly: boolean
  unreadChatMessages: any[]
  billingReviewPending?: boolean
  billingDialogEnabledForUser?: boolean
  onShowBillingDialog?: () => void
  readinessEnabled?: boolean
  isConsultationReady?: boolean
  isTogglingReadiness?: boolean
  onToggleReadiness?: () => void
  checklist?: ReadinessChecklistState
}

export function Topbar({
  authUser,
  consultation,
  consultationId,
  doctor,
  isConsultationActive,
  isDoctorOnline,
  isDoctor,
  isPdsOnline,
  pds,
  readOnly,
  unreadChatMessages,
  billingReviewPending,
  billingDialogEnabledForUser,
  onShowBillingDialog,
  readinessEnabled,
  isConsultationReady,
  isTogglingReadiness,
  onToggleReadiness,
  checklist,
}: TopbarProps) {
  const { topbarContentContainer } = useDiagnosticLayout()
  const dispatch = useAppDispatch()
  const isPDS = isStaff(authUser) && authUser?.status === 'PDS'

  // Dialog state
  const [openParticipantsDialog, setOpenParticipantsDialog] = useState(false)
  const [messengerOpen, setMessengerOpen] = useState(false)
  const [endLiveConsultationDialogOpen, setEndLiveConsultationDialogOpen] = useState(false)

  // Mark messages as read when messenger is open
  useEffect(() => {
    if (consultation.chatRoom && messengerOpen && unreadChatMessages.length) {
      dispatch(confirmReadingMessages(consultation.chatRoom))
    }
  }, [consultation.chatRoom, dispatch, messengerOpen, unreadChatMessages.length])

  const handleToggleMessenger = useCallback(() => {
    setMessengerOpen((prev) => !prev)
  }, [])

  const handleCloseEndConsultationDialog = useCallback(() => {
    setEndLiveConsultationDialogOpen(false)
  }, [])

  return (
    <Portal container={topbarContentContainer}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
        <ParticipantInfo
          authUser={authUser}
          consultation={consultation}
          doctor={doctor}
          isDoctorOnline={isDoctorOnline}
          isPdsOnline={isPdsOnline}
          pds={pds}
        />
        <Stack direction="row" alignItems="center" spacing={2}>
          <MeetingClockComponent startDate={consultation.startDate} consultationStatus={ConsultationStatus.Active} />
          <NetworkSpeed />
          {!readOnly && <InviteButton onClick={() => setOpenParticipantsDialog((prev) => !prev)} />}
          <MessengerButton unreadCount={unreadChatMessages.length} onClick={handleToggleMessenger} />
          {isDoctor && consultation?.waitingRoom && <WaitingPatientsMiniView waitingRoomId={consultation.waitingRoom} />}
          {isPDS && consultation?.waitingRoom && consultation.status === ConsultationStatus.Scheduled && readinessEnabled && (
            <>
              {checklist && <ReadinessChecklistButton checklist={checklist} />}
              <MarkReadyButton ready={!!isConsultationReady} disabled={!!isTogglingReadiness} onClick={onToggleReadiness} />
            </>
          )}
          {billingDialogEnabledForUser && consultation.status === ConsultationStatus.Completed && (
            <BillingButton billingReviewPending={!!billingReviewPending} onClick={onShowBillingDialog} />
          )}
          <EndConsultationButton
            isConsultationActive={isConsultationActive}
            onEndConsultation={() => {
              setMessengerOpen(false)
              setEndLiveConsultationDialogOpen(true)
            }}
          />
        </Stack>
      </Stack>

      {/* Dialogs */}
      {!readOnly && <InvitationDialog open={openParticipantsDialog} consultation={consultation} onClose={() => setOpenParticipantsDialog(false)} />}
      <EndLiveConsultationDialog
        onClose={handleCloseEndConsultationDialog}
        open={endLiveConsultationDialogOpen}
        billingReviewPending={!!billingReviewPending && consultation.status === ConsultationStatus.Completed}
        onReviewBilling={() => {
          setEndLiveConsultationDialogOpen(false)
          onShowBillingDialog?.()
        }}
      />
      <Messenger chatRoomId={consultation.chatRoom} onClose={handleToggleMessenger} open={messengerOpen} />
    </Portal>
  )
}

function ParticipantInfo({
  authUser,
  consultation,
  doctor,
  isDoctorOnline,
  isPdsOnline,
  pds,
}: {
  authUser: AuthUser
  consultation: Consultation
  doctor: string
  isDoctorOnline: boolean
  isPdsOnline: boolean
  pds: string
}) {
  const { t } = useTranslation()

  const isViewingPds = authUser._id === consultation.medecin?._id

  if (isViewingPds) {
    return (
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
        <PdsIcon
          isFemale={consultation.pds?.userInfo.gender === 'F'}
          htmlColor="#FFF"
          height={'45px'}
          width={'45px'}
          sx={{
            backgroundColor: 'white',
            height: '45px',
            width: '45px',
            border: '3px solid',
            borderColor: isPdsOnline ? colors.green400 : colors.red500,
            borderRadius: '40px',
          }}
        />
        <Stack direction="column">
          <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
            {`${pds}`}
          </Typography>
          <Typography fontSize="10px" fontWeight="300" sx={{ color: (theme) => theme.palette.common.white }}>
            {isPdsOnline ? t('common:online') : t('common:offline')}
          </Typography>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
      <DoctorIcon
        isFemale={consultation.medecin?.userInfo.gender === 'F'}
        htmlColor="#FFF"
        height={'45px'}
        width={'45px'}
        sx={{
          backgroundColor: 'white',
          height: '45px',
          width: '45px',
          border: '3px solid',
          borderColor: isDoctorOnline ? colors.green400 : colors.red500,
          borderRadius: '40px',
        }}
      />
      <Stack direction="column">
        <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
          Dr. {`${doctor}`}
        </Typography>
        <Typography fontSize="10px" fontWeight="300" sx={{ color: (theme) => theme.palette.common.white }}>
          {isDoctorOnline ? t('common:online') : t('common:offline')}
        </Typography>
      </Stack>
    </Stack>
  )
}

function InviteButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()

  return (
    <Tooltip title={t('layouts.diagnostic.header.openParticipantsButton.tooltip')}>
      <TopbarButton id="invite-button" onClick={onClick} variant="contained">
        <PersonAddAltIcon />
      </TopbarButton>
    </Tooltip>
  )
}

function MessengerButton({ unreadCount, onClick }: { unreadCount: number; onClick: () => void }) {
  const { t } = useTranslation()

  return (
    <Badge badgeContent={unreadCount} color="error" max={9} overlap="rectangular">
      <Tooltip title={t('page.liveConsultation.openMessengerButton.tooltip')}>
        <TopbarButton id="chat-button" onClick={onClick} variant="contained">
          <ChatOutlinedIcon />
        </TopbarButton>
      </Tooltip>
    </Badge>
  )
}

function EndConsultationButton({ isConsultationActive, onEndConsultation }: { isConsultationActive: boolean; onEndConsultation: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isNetsoinsConsultation = false // TODO: add

  const handleClick = useCallback(() => {
    if (isNetsoinsConsultation) {
      navigate('/netsoins/0')
    } else {
      onEndConsultation()
    }
  }, [isNetsoinsConsultation, navigate, onEndConsultation])

  return (
    <Button
      color="error"
      onClick={handleClick}
      size="small"
      variant="contained"
      sx={{
        borderRadius: '15px',
        fontSize: '12px',
      }}
      data-testid="end-consultation-button"
    >
      {t(`page.liveConsultation.topbar.${isConsultationActive ? 'endConsultationButton' : 'quitConsultationButton'}.label`)}
    </Button>
  )
}

export function BillingButton({ billingReviewPending, onClick }: { billingReviewPending: boolean; onClick?: () => void }) {
  const { t } = useTranslation()

  return (
    <Tooltip
      title={t(
        billingReviewPending
          ? 'page.liveConsultation.topbar.billingPending.tooltip'
          : 'page.liveConsultation.topbar.billingValidated.tooltip',
        { defaultValue: billingReviewPending ? 'Facturation à valider' : 'Modifier la facturation' }
      )}
    >
      <Button
        color={billingReviewPending ? 'warning' : 'success'}
        onClick={onClick}
        size="small"
        variant="contained"
        startIcon={<ReceiptLongIcon />}
        sx={{ borderRadius: '15px', fontSize: '12px' }}
      >
        {t('page.liveConsultation.topbar.billingPending.label', { defaultValue: 'Facturation' })}
      </Button>
    </Tooltip>
  )
}

export function MarkReadyButton({ ready, disabled, onClick }: { ready: boolean; disabled?: boolean; onClick?: () => void }) {
  const { t } = useTranslation()

  return (
    <Tooltip title={t('page.liveConsultation.topbar.markReady.tooltip')}>
      <Button
        color={ready ? 'success' : 'warning'}
        onClick={onClick}
        size="small"
        variant="contained"
        startIcon={<CheckCircleOutlineIcon />}
        sx={{ borderRadius: '15px', fontSize: '12px' }}
        disabled={disabled}
        data-testid="mark-ready-button"
      >
        {t(ready ? 'page.liveConsultation.topbar.markReady.ready' : 'page.liveConsultation.topbar.markReady.label')}
      </Button>
    </Tooltip>
  )
}
